'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { dialogVariants } from '@/lib/animations';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import AgreementModal from '@/components/ui/AgreementModal';
import { splitAgreementTemplate } from '@/lib/agreement-text';
import {
  loadRecaptchaScript,
  renderRecaptchaWidget,
  resetRecaptchaWidget,
  getRecaptchaToken,
  loadRecaptchaV3Script,
  getRecaptchaV3Token,
  loadTurnstileScript,
  renderTurnstileWidget,
  resetTurnstileWidget,
  getTurnstileToken,
  removeTurnstileWidget,
} from '@/lib/captcha';

const FORM_SLUG = 'brochure-download-form';
const CAPTCHA_CONTAINER_ID = 'brochure-modal-captcha-container';

export type BrochureSource = 'tour_card' | 'yacht_card' | 'product_detail';

export interface BrochureDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  productType: 'tour' | 'yacht';
  source?: BrochureSource;
  /** Oturum açıksa müşteri e-postası (opsiyonel) */
  prefillEmail?: string | null;
  /** API yanıt mesajlarının dilini belirler (opsiyonel) */
  languageId?: number | null;
}

type SubmitStatus = 'idle' | 'loading' | 'link_sent' | 'verification_sent' | 'job_queued' | 'error';

interface CaptchaConfig {
  is_enabled: boolean;
  site_key: string | null;
  provider: string | null;
  script_url: string | null;
}

interface BrochureAgreement {
  id: number;
  title: string;
  slug: string;
  is_required: boolean;
}

interface FormDetails {
  id: number;
  title: string;
  slug: string;
  fields: Array<{ name: string; type: string; label?: string; required?: boolean }>;
  agreements?: { required: BrochureAgreement[]; optional: BrochureAgreement[] };
  captcha_config?: CaptchaConfig;
}

export default function BrochureDownloadModal({
  isOpen,
  onClose,
  productId,
  productType,
  source = 'tour_card',
  prefillEmail = null,
  languageId: propLanguageId = null,
}: BrochureDownloadModalProps) {
  const { getVariable } = useLanguageVariables();
  const t = (key: string, fallback: string = key) => getVariable(key, '') || fallback;

  const [email, setEmail] = useState('');
  const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>({});
  const [formDetails, setFormDetails] = useState<FormDetails | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const captchaWidgetIdRef = useRef<number | string | null>(null);

  const requiredAgreements = formDetails?.agreements?.required ?? [];
  const optionalAgreements = formDetails?.agreements?.optional ?? [];
  const allRequiredAccepted = requiredAgreements.every((a) => agreementsState[a.id] === true);
  const captchaConfig = formDetails?.captcha_config;

  // Sözleşme içerik modalı
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<BrochureAgreement | null>(null);
  const openAgreement = (a: BrochureAgreement) => { setSelectedAgreement(a); setAgreementModalOpen(true); };

  // Sözleşme onay metnini çözer: agreement.text.{slug} -> agreement.{required|optional}.default
  const resolveAgreementTemplate = (a: BrochureAgreement): string => {
    const specific = getVariable(`agreement.text.${a.slug}`, '');
    if (specific) return specific;
    return a.is_required
      ? getVariable('agreement.required.default', '{agreement}')
      : getVariable('agreement.optional.default', '{agreement}');
  };
  const renderAgreementLabel = (a: BrochureAgreement) => {
    const { before, after } = splitAgreementTemplate(resolveAgreementTemplate(a));
    return (
      <span className="text-sm text-gray-600">
        {before}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAgreement(a); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openAgreement(a); } }}
          className="text-alsero-first font-semibold underline cursor-pointer hover:opacity-80"
        >
          {a.title}
        </span>
        {after}
      </span>
    );
  };
  const isCaptchaRequired = Boolean(captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider);
  const canSubmit = email.trim().length > 0 && allRequiredAccepted;

  const fetchFormDetails = useCallback(async () => {
    setFormLoading(true);
    try {
      const params = new URLSearchParams({ form_slug: FORM_SLUG });
      const res = await fetch(`/api/forms/details?${params.toString()}`);
      const payload = await res.json();
      if (payload?.success && payload?.data) {
        const data = payload.data as FormDetails;
        setFormDetails(data);
        const initial: Record<number, boolean> = {};
        (data.agreements?.required ?? []).forEach((a) => { initial[a.id] = false; });
        (data.agreements?.optional ?? []).forEach((a) => { initial[a.id] = false; });
        setAgreementsState(initial);
      } else {
        setFormDetails(null);
      }
    } catch {
      setFormDetails(null);
    } finally {
      setFormLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSubmitStatus('idle');
      setErrorMessage(null);
      setEmail(prefillEmail ?? '');
      fetchFormDetails();
    }
  }, [isOpen, prefillEmail, fetchFormDetails]);

  useEffect(() => {
    if (!isOpen || !formDetails?.captcha_config?.is_enabled || !formDetails.captcha_config?.site_key || !formDetails.captcha_config?.provider) return;
    const provider = formDetails.captcha_config.provider;
    const siteKey = formDetails.captcha_config.site_key;
    const scriptUrl = formDetails.captcha_config.script_url ?? undefined;
    const initCaptcha = async () => {
      try {
        if (provider === 'google_recaptcha_v2') {
          await loadRecaptchaScript(scriptUrl);
          const checkContainer = setInterval(() => {
            const container = document.getElementById(CAPTCHA_CONTAINER_ID);
            if (container && siteKey) {
              clearInterval(checkContainer);
              renderRecaptchaWidget(CAPTCHA_CONTAINER_ID, siteKey, () => {}).then((id) => {
                captchaWidgetIdRef.current = id;
              }).catch(() => {});
            }
          }, 100);
          return () => clearInterval(checkContainer);
        }
        if (provider === 'cloudflare_turnstile') {
          await loadTurnstileScript(scriptUrl);
          const checkContainer = setInterval(() => {
            const container = document.getElementById(CAPTCHA_CONTAINER_ID);
            if (container && siteKey) {
              clearInterval(checkContainer);
              renderTurnstileWidget(CAPTCHA_CONTAINER_ID, siteKey, () => {}).then((id) => {
                captchaWidgetIdRef.current = id;
              }).catch(() => {});
            }
          }, 100);
          return () => clearInterval(checkContainer);
        }
      } catch {
        // ignore
      }
    };
    initCaptcha();
    return () => {
      if (captchaWidgetIdRef.current != null && provider === 'google_recaptcha_v2') {
        resetRecaptchaWidget(captchaWidgetIdRef.current as number);
      } else if (captchaWidgetIdRef.current != null && provider === 'cloudflare_turnstile') {
        resetTurnstileWidget(captchaWidgetIdRef.current as string);
        removeTurnstileWidget(captchaWidgetIdRef.current as string);
      }
      captchaWidgetIdRef.current = null;
    };
  }, [isOpen, formDetails?.captcha_config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitStatus === 'loading') return;
    setSubmitStatus('loading');
    setErrorMessage(null);
    let recaptchaToken = '';
    if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
      if (captchaConfig.provider === 'google_recaptcha_v2') {
        recaptchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || '';
        if (!recaptchaToken) {
          setSubmitStatus('idle');
          setErrorMessage(t('common.captcha_required'));
          return;
        }
      } else if (captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') {
        try {
          await loadRecaptchaV3Script(
            captchaConfig.site_key,
            captchaConfig.provider === 'google_recaptcha_enterprise' ? captchaConfig.script_url ?? undefined : undefined
          );
          recaptchaToken = await getRecaptchaV3Token(captchaConfig.site_key, 'brochure_download');
        } catch {
          setSubmitStatus('idle');
          setErrorMessage(t('common.captcha_required'));
          return;
        }
      } else if (captchaConfig.provider === 'cloudflare_turnstile') {
        recaptchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || '';
        if (!recaptchaToken) {
          setSubmitStatus('idle');
          setErrorMessage(t('common.captcha_required'));
          return;
        }
      }
    }
    try {
      const body: Record<string, unknown> = {
        email: email.trim(),
        product_id: productId,
        form_slug: FORM_SLUG,
        source,
      };
      if (propLanguageId != null) body.language_id = propLanguageId;
      const agreementsPayload = [
        ...requiredAgreements.map((a) => ({ id: a.id, accepted: agreementsState[a.id] === true, required: true })),
        ...optionalAgreements.filter((a) => agreementsState[a.id]).map((a) => ({ id: a.id, accepted: true, required: false })),
      ];
      if (agreementsPayload.length) body.agreements = agreementsPayload;
      if (recaptchaToken) (body as Record<string, string>).recaptcha_token = recaptchaToken;

      const sendRequest = () => fetch('/api/brochure-download/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let res = await sendRequest();
      let data = await res.json().catch(() => ({}));

      if (
        !res.ok &&
        data?.code === 'captcha.error.browser' &&
        captchaConfig?.provider === 'google_recaptcha_enterprise' &&
        captchaConfig.site_key
      ) {
        const retryToken = await getRecaptchaV3Token(captchaConfig.site_key, 'brochure_download');
        body.recaptcha_token = retryToken;
        res = await sendRequest();
        data = await res.json().catch(() => ({}));
      }

      if (!res.ok) {
        setSubmitStatus('error');
        setErrorMessage(data?.message ?? t('brochure_download.error', 'Request failed. Please try again.'));
        if (captchaConfig?.provider === 'google_recaptcha_v2' && captchaWidgetIdRef.current != null) {
          resetRecaptchaWidget(captchaWidgetIdRef.current as number);
        } else if (captchaConfig?.provider === 'cloudflare_turnstile' && captchaWidgetIdRef.current != null) {
          resetTurnstileWidget(captchaWidgetIdRef.current as string);
        }
        return;
      }

      const status = data?.status as string | undefined;
      if (status === 'link_sent') setSubmitStatus('link_sent');
      else if (status === 'verification_sent') setSubmitStatus('verification_sent');
      else if (status === 'job_queued') setSubmitStatus('job_queued');
      else setSubmitStatus('idle');
    } catch {
      setSubmitStatus('error');
      setErrorMessage(t('brochure_download.error', 'Request failed. Please try again.'));
    }
  };

  const handleClose = useCallback(() => {
    setSubmitStatus('idle');
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          className="absolute inset-0 bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          aria-hidden
        />
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="brochure-modal-title"
          className="relative bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          variants={dialogVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <h2 id="brochure-modal-title" className="text-lg font-semibold text-gray-900">
              {formDetails?.title ?? t('brochure_download.title', 'Download Brochure')}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label={t('brochure_download.close', 'Close')}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {submitStatus === 'link_sent' && (
              <div className="text-center py-4">
                <div className="inline-flex w-14 h-14 rounded-full bg-green-100 items-center justify-center mb-3">
                  <DocumentArrowDownIcon className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-gray-700 font-medium">
                  {t('brochure_download.link_sent', 'The brochure download link has been sent to your email.')}
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="mt-4 px-5 py-2.5 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90 transition-colors"
                >
                  {t('brochure_download.close', 'Close')}
                </button>
              </div>
            )}

            {(submitStatus === 'verification_sent' || submitStatus === 'job_queued') && (
              <div className="text-center py-4">
                <p className="text-gray-700 font-medium">
                  {submitStatus === 'verification_sent'
                    ? t('brochure_download.verification_sent', 'A verification link has been sent to your email. After verifying, you will receive the brochure download link.')
                    : t('brochure_download.job_queued', 'The brochure is being prepared. You will receive the download link by email when it is ready.')}
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="mt-4 px-5 py-2.5 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90 transition-colors"
                >
                  {t('brochure_download.close', 'Close')}
                </button>
              </div>
            )}

            {(submitStatus === 'idle' || submitStatus === 'loading') && (
              <>
                {formLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="brochure-email" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('brochure_download.email_label', 'Email')}
                      </label>
                      <input
                        id="brochure-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first focus:border-transparent"
                        placeholder={t('brochure_download.email_placeholder', 'your@email.com')}
                        autoComplete="email"
                      />
                    </div>
                    {requiredAgreements.length > 0 && (
                      <div className="space-y-2">
                        {requiredAgreements.map((a) => (
                          <label key={a.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={agreementsState[a.id] === true}
                              onChange={(e) => setAgreementsState((prev) => ({ ...prev, [a.id]: e.target.checked }))}
                              className="mt-1 rounded border-gray-300 text-alsero-first focus:ring-alsero-first"
                            />
                            {renderAgreementLabel(a)}
                          </label>
                        ))}
                      </div>
                    )}
                    {optionalAgreements.length > 0 && (
                      <div className="space-y-2">
                        {optionalAgreements.map((a) => (
                          <label key={a.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={agreementsState[a.id] === true}
                              onChange={(e) => setAgreementsState((prev) => ({ ...prev, [a.id]: e.target.checked }))}
                              className="mt-1 rounded border-gray-300 text-alsero-first focus:ring-alsero-first"
                            />
                            {renderAgreementLabel(a)}
                          </label>
                        ))}
                      </div>
                    )}
                    {captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider && (
                      <div className="space-y-1">
                        {(captchaConfig.provider === 'google_recaptcha_v2' || captchaConfig.provider === 'cloudflare_turnstile') && (
                          <div id={CAPTCHA_CONTAINER_ID} className="min-h-[78px]" />
                        )}
                        {(captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') && (
                          <p className="text-sm text-gray-500">{t('common.captcha_auto_note')}</p>
                        )}
                      </div>
                    )}
                    {errorMessage && (
                      <p className="text-sm text-red-600">{errorMessage}</p>
                    )}
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        {t('brochure_download.cancel', 'Cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={!canSubmit || submitStatus === 'loading'}
                        className="flex-1 px-4 py-2.5 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {submitStatus === 'loading' ? (
                          <>
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {t('brochure_download.sending', 'Sending...')}
                          </>
                        ) : (
                          t('brochure_download.submit', 'Send me the brochure')
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}

            {submitStatus === 'error' && (
              <div className="space-y-4">
                <p className="text-red-600">{errorMessage}</p>
                <button
                  type="button"
                  onClick={() => { setSubmitStatus('idle'); setErrorMessage(null); }}
                  className="w-full px-4 py-2.5 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90"
                >
                  {t('brochure_download.retry', 'Try again')}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
    <AgreementModal
      isOpen={agreementModalOpen}
      onClose={() => { setAgreementModalOpen(false); setSelectedAgreement(null); }}
      agreementSlug={selectedAgreement?.slug ?? null}
      agreementId={selectedAgreement?.id}
      isRequired={selectedAgreement?.is_required ?? false}
      onAccept={(id) => setAgreementsState((prev) => ({ ...prev, [id]: true }))}
    />
    </>
  );
}
